import type { PlanId, Plan, PlanPrice } from '@/types';

const DEV_MODE = process.env.NEXT_PUBLIC_DEV_MODE === 'true';

// Rule limits by plan (applies separately to categorization and naming rules)
export const ruleLimits: Record<PlanId, number> = {
  starter: 30,
  premium: 100,
  'premium-plus': 100,
  pro: 100000,
  'pro-plus': 100000,
  free: 5,
  hibernation: 5,
};

// Category limits by plan (total custom categories across income and expense)
export const categoryLimits: Record<PlanId, number> = {
  starter: 30,
  premium: 100,
  'premium-plus': 100,
  pro: 100000,
  'pro-plus': 100000,
  free: 5,
  hibernation: 5,
};

// Workspace limits by plan (Phase 3, D8). `premium` is the "business" tier (D2):
// 2 workspaces (Personal + Business). Backend-authoritative via GET
// /api/v2/config/limits; this SSR mirror is parity-guarded
// (apps/backend/tests/unit/test_plan_limits_parity.py).
export const workspaceLimits: Record<PlanId, number> = {
  free: 1,
  // Hibernation's promise is "your data stays exactly as you left it", so its
  // workspace cap matches the largest plan — hibernating must never lock a
  // workspace the user could see on their old plan.
  hibernation: 25,
  starter: 1,
  premium: 2,
  'premium-plus': 5,
  pro: 10,
  'pro-plus': 25,
};

// Email forwarding limits (workspace email address model — one address per
// workspace, so there is no address count). Backend-authoritative via
// GET /api/v2/config/limits; parity-guarded
// (apps/backend/tests/unit/test_plan_limits_parity.py).
//  - verified senders: extra senders ("collaborators") per workspace,
//    Premium+ and up (backend VERIFIED_SENDER_LIMITS).
//  - connected mailboxes: mailboxes the owner may connect for automatic
//    forwarding, pooled across workspaces (backend AUTO_FORWARDING_CONNECTION_LIMITS).
export const verifiedSenderLimits: Record<PlanId, number> = {
  free: 0,
  hibernation: 0,
  starter: 0,
  premium: 0,
  'premium-plus': 5,
  pro: 5,
  'pro-plus': 10,
};

export const connectedMailboxLimits: Record<PlanId, number> = {
  free: 0,
  hibernation: 0,
  starter: 0,
  premium: 2,
  'premium-plus': 5,
  pro: 5,
  'pro-plus': 10,
};

// Workflow-automation limits by plan (Phase 6, #7). Max enabled automations.
// Automations are a paid value feature (free 0). Backend-authoritative via
// GET /api/v2/config/limits; parity-guarded
// (apps/backend/tests/unit/test_plan_limits_parity.py).
export const workflowLimits: Record<PlanId, number> = {
  free: 0,
  hibernation: 0,
  starter: 3,
  premium: 10,
  'premium-plus': 15,
  pro: 50,
  'pro-plus': 100000,
};

interface TopUpPackage {
  id: string;
  name: string;
  description: string;
  amount: number;
  display: string;
  credits: number;
  stripeId: string;
  targetPlan: PlanId;
}

interface StripePriceInfo {
  stripeId: string;
  planId: string;
  name: string;
  frequency: string;
  credits: number;
  monthlyCredits: number;
  isTopUp?: boolean;
  targetPlan?: PlanId;
}

interface StripePriceMapEntry {
  id: string;
  name: string;
  monthlyCredits: number;
  frequency: string;
  isTopUp: boolean;
  targetPlan?: PlanId;
}

// Centralized plan configuration
export const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Basic AI bookkeeping. Good for personal use or new freelancers.',
    features: [
      'Process 100 pages per month',
      'Up to 30 Custom Categories',
      'Up to 30 Custom Rules',
      'Basic Currency Conversion',
      'Custom Document Renaming',
      'Auto Duplicate Detection',
      'Forward receipts by email',
      'Monthly Email Reports',
      'Email support',
    ],
    prices: {
      monthly: {
        amount: 19,
        display: '$19',
        // Live: $19/mo (repriced 2026-08-19). The previous $12/mo live price is
        // kept as a legacy id so grandfathered subscribers keep renewing with
        // full Starter entitlements; new checkouts always use `stripeId`.
        stripeId: DEV_MODE ? 'price_1NvAKRFVyAPv59KMCWlm24En' : 'price_1NCcnkFVyAPv59KMLRWJFWU6',
        legacyStripeIds: DEV_MODE ? [] : ['price_1NI4xlFVyAPv59KMrVNYO0U7'],
        credits: 100,
        monthlyCredits: 100,
      },
      yearly: {
        amount: 144,
        display: '$144',
        // Live: $144/yr (repriced 2026-08-19). Previous $108/yr live price kept
        // as legacy so grandfathered subscribers keep renewing with full
        // Starter entitlements; new checkouts always use `stripeId`.
        stripeId: DEV_MODE ? 'price_1RJrkEFVyAPv59KMzoWz9ypE' : 'price_1U66HWFVyAPv59KMyIAZuCNU',
        legacyStripeIds: DEV_MODE ? [] : ['price_1RKfkVFVyAPv59KMHx1AuCDl'],
        credits: 1440,
        monthlyCredits: 100,
      },
    },
    mostPopular: false,
    disabled: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Bookkeeping on autopilot. Perfect for freelancers and small business.',
    features: [
      'Process 500 pages per month',
      'Up to 100 Custom Categories',
      'Up to 100 Custom Rules',
      'Historical Currency Conversion',
      'Custom Document Renaming',
      'Auto Duplicate Detection',
      'Connect 2 mailboxes for auto-forwarding',
      '2 Workspaces (Personal + Business)',
      'Premium Support',
    ],
    prices: {
      monthly: {
        amount: 29,
        display: '$29',
        stripeId: DEV_MODE ? 'price_1RKET0FVyAPv59KM62CZ5Vox' : 'price_1NI51TFVyAPv59KM6Ngi0wxn',
        credits: 500,
        monthlyCredits: 500,
      },
      yearly: {
        amount: 288,
        display: '$288',
        stripeId: DEV_MODE ? 'price_1RKEU0FVyAPv59KMfoaHZU5f' : 'price_1RKfm2FVyAPv59KMpdAP9RDG',
        credits: 6000,
        monthlyCredits: 500,
      },
    },
    mostPopular: true,
    disabled: false,
  },
  {
    id: 'premium-plus',
    name: 'Premium+',
    description: 'Everything you need to scale. Ideal for growing businesses with higher volume.',
    features: [
      'Process 1500 pages per month',
      'Up to 100 Custom Categories',
      'Up to 100 Custom Rules',
      'Historical Currency Conversion',
      'Custom Document Renaming',
      'Auto Duplicate Detection',
      'Your own email address (acme-books@receiptsai.com)',
      'Connect 5 mailboxes for auto-forwarding',
      'Up to 5 senders — email receipts in, nothing to log into',
      '5 Workspaces',
      'Invite 2 users to work in your books',
      'Monthly Email Reports',
      'Premium Support',
    ],
    prices: {
      monthly: {
        amount: 49,
        display: '$49',
        stripeId: DEV_MODE ? 'price_1TvD0KFVyAPv59KMaf38Uap9' : 'price_1TvCvNFVyAPv59KM9b1cwz5a',
        credits: 1500,
        monthlyCredits: 1500,
      },
      yearly: {
        amount: 468,
        display: '$468',
        stripeId: DEV_MODE ? 'price_1TvD0jFVyAPv59KMQgDxwnJl' : 'price_1TvCwrFVyAPv59KMupV6Fntj',
        credits: 18000,
        monthlyCredits: 1500,
      },
    },
    mostPopular: false,
    disabled: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Get more credits and priority support, perfect for accountants and industry professionals.',
    features: [
      'Process 3000 pages per month',
      'Up to 100 Custom Categories',
      'Unlimited Custom Rules',
      'Historical Currency Conversion',
      'Custom Document Renaming',
      'Auto Duplicate Detection',
      'Connect 5 mailboxes for auto-forwarding',
      '10 Workspaces',
      'Priority Processing',
      'Priority Support',
    ],
    prices: {
      monthly: {
        amount: 99,
        display: '$99',
        stripeId: DEV_MODE ? 'price_1RsIcYFVyAPv59KMVONRuOhx' : 'price_1Rt3IUFVyAPv59KMJU2O8RZz',
        credits: 3000,
        monthlyCredits: 3000,
      },
      yearly: {
        amount: 948,
        display: '$948',
        stripeId: DEV_MODE ? 'price_1RsIgKFVyAPv59KMPQjvWYlW' : 'price_1Rt3NMFVyAPv59KM5mLagMnh',
        credits: 3000,
        monthlyCredits: 3000,
      },
    },
    mostPopular: false,
    disabled: false,
  },
  {
    id: 'pro-plus',
    name: 'Pro+',
    description: 'Get our highest level of support and processing power, perfect for enterprise or professionals.',
    features: [
      'Process 10000 pages per month',
      'Up to 100 Custom Categories',
      'Unlimited Custom Rules',
      'Historical Currency Conversion',
      'Custom Document Renaming',
      'Auto Duplicate Detection',
      'Connect 10 mailboxes for auto-forwarding',
      '25 Workspaces',
      'Founder-level Support',
    ],
    prices: {
      monthly: {
        amount: 199,
        display: '$199',
        stripeId: DEV_MODE ? 'price_1RsIezFVyAPv59KMwsE5DZe0' : 'price_1Rt3JFFVyAPv59KMxOsohAyl',
        credits: 10000,
        monthlyCredits: 10000,
      },
      yearly: {
        amount: 1788,
        display: '$1788',
        stripeId: DEV_MODE ? 'price_1RsIm6FVyAPv59KM25EytBuZ' : 'price_1Rt3NwFVyAPv59KMGNnTxZFY',
        credits: 10000,
        monthlyCredits: 10000,
      },
    },
    mostPopular: false,
    disabled: false,
  },
];

// ---------------------------------------------------------------------------
// Premium+ — now live and part of `plans` above (its Stripe prices are wired:
// test ids via DEV_MODE, live ids otherwise). Because it lives in `plans`, both
// server flows pick it up automatically: /api/stripe/checkout resolves it, and
// the webhook's `stripePriceIdToPlan` map (built from `plans`) provisions it on
// payment. This aliased export is kept for the upgrade modals, which splice
// Premium+ into their display order by reference.
// ---------------------------------------------------------------------------
export const premiumPlusPlan: Plan = plans.find((p) => p.id === 'premium-plus')!;

// A plan can only be checked out when the price the user is actually buying has a
// Stripe id. Guards every upgrade CTA, so an unwired plan degrades to "coming
// soon" instead of a failed checkout session.
export const isPurchasable = (plan: Plan, frequency: 'monthly' | 'yearly'): boolean =>
  Boolean(plan.prices[frequency]?.stripeId);

// ---------------------------------------------------------------------------
// Hibernation — the retention plan offered in the cancel flow ("I don't need
// it anymore"). $5/mo, 10 credits/mo, keeps all data/workspaces/rules/email
// address. Deliberately NOT in `plans[]`: it must never appear in the upgrade
// modals or be resolvable by /api/stripe/checkout — the only way onto it is
// /api/retention/hibernate swapping an existing subscription's price. Its
// price id is appended to `allStripePrices` below so the webhook provisions
// it like any other plan on the next paid invoice.
// Live price id is intentionally empty until created in the live Stripe
// account (the hibernate route refuses with a config error until then).
// ---------------------------------------------------------------------------
export const hibernationPlan = {
  id: 'hibernation' as PlanId,
  name: 'Hibernation',
  amount: 5,
  display: '$5',
  stripeId: DEV_MODE ? 'price_1U8xMsFVyAPv59KM9P7pTZE8' : '',
  monthlyCredits: 10,
};

// Top-up credit packages
export const topUpPackages: TopUpPackage[] = [
  {
    id: 'starter-topup',
    name: 'Starter Top-up',
    description: 'Add 100 credits to your account',
    amount: 13.99,
    display: '$13.99',
    credits: 100,
    // TODO: Replace with actual Stripe price IDs once created
    stripeId: DEV_MODE ? 'price_1RmADXFVyAPv59KMiXZGMkSl' : 'price_1RmB26FVyAPv59KM5xxdRv6T',
    targetPlan: 'starter',
  },
  {
    id: 'premium-topup',
    name: 'Premium Top-up',
    description: 'Add 500 credits to your account',
    amount: 30,
    display: '$30',
    credits: 500,
    // TODO: Replace with actual Stripe price IDs once created
    stripeId: DEV_MODE ? 'price_1RmACcFVyAPv59KM5p8XWUar' : 'price_1RmB1UFVyAPv59KMrhM5nPfR',
    targetPlan: 'premium',
  },
  {
    id: 'pro-topup',
    name: 'Pro Top-up',
    description: 'Add 1000 credits to your account',
    amount: 34.99,
    display: '$34.99',
    credits: 1000,
    stripeId: DEV_MODE ? 'price_1Rt40hFVyAPv59KMYkXxrbgM' : 'price_1Rt3ilFVyAPv59KM5siBxEgu',
    targetPlan: 'pro',
  },
];

// Comprehensive source for all Stripe Price IDs (including placeholders and future archived IDs)
// This should be the single source of truth for webhook lookups.
const allStripePrices: StripePriceInfo[] = [
  // Regular subscription plans. Each (plan, frequency) contributes its current
  // checkout price plus any legacy price ids still held by existing
  // subscribers, so the webhook map resolves both.
  ...plans.flatMap((plan) =>
    (['monthly', 'yearly'] as const).flatMap((frequency) => {
      const price = plan.prices[frequency];
      return [price.stripeId, ...(price.legacyStripeIds ?? [])].map((stripeId) => ({
        stripeId,
        planId: plan.id,
        name: plan.name,
        frequency,
        credits: price.credits,
        monthlyCredits: price.monthlyCredits,
      }));
    })
  ),
  // Hibernation retention plan (cancel flow). Empty stripeId in live mode is
  // dropped by the Boolean filter below until the live price exists.
  {
    stripeId: hibernationPlan.stripeId,
    planId: hibernationPlan.id,
    name: hibernationPlan.name,
    frequency: 'monthly',
    credits: hibernationPlan.monthlyCredits,
    monthlyCredits: hibernationPlan.monthlyCredits,
  },
  // Top-up packages
  ...topUpPackages.map((topUp) => ({
    stripeId: topUp.stripeId,
    planId: topUp.id,
    name: topUp.name,
    frequency: 'one-time',
    credits: topUp.credits,
    monthlyCredits: topUp.credits, // For top-ups, credits = monthlyCredits
    isTopUp: true,
    targetPlan: topUp.targetPlan,
  })),
].filter((price) => Boolean(price.stripeId)) as StripePriceInfo[];

// Helper map for quick lookup by Stripe Price ID - Built from the comprehensive source
export const stripePriceIdToPlan: Record<string, StripePriceMapEntry> = allStripePrices.reduce(
  (map, priceInfo) => {
    if (priceInfo.stripeId) {
      map[priceInfo.stripeId] = {
        id: priceInfo.planId,
        name: priceInfo.name,
        monthlyCredits: priceInfo.monthlyCredits,
        frequency: priceInfo.frequency,
        isTopUp: priceInfo.isTopUp || false,
        targetPlan: priceInfo.targetPlan,
      };
    }
    return map;
  },
  {} as Record<string, StripePriceMapEntry>
);

export { DEV_MODE };
